<template>
  <div class="vin-mask-rule-panel" v-loading="loading">
    <section class="panel-header mask-panel-header">
      <div>
        <h3>无效 VIN 掩码</h3>
        <p>按 VIN 前 13 位维护通配掩码，“.” 表示任意一个字符，第 14-17 位默认忽略。</p>
      </div>

      <div class="header-actions">
        <el-tag type="success" effect="plain">保存成功可用于下载</el-tag>
        <el-button :icon="Refresh" plain :loading="loading" @click="fetchGroups">
          刷新
        </el-button>
      </div>
    </section>

    <el-alert
      class="online-alert"
      title="规则保存到 Redis 后，可在“未匹配模板下载过滤设置”中通过“过滤无效 VIN 掩码”开关控制是否应用。"
      type="success"
      :closable="false"
      show-icon
    />

    <section class="mask-summary-grid" aria-label="掩码规则摘要">
      <article class="mask-summary-card">
        <span>规则组</span>
        <strong>{{ maskGroups.length }}</strong>
        <small>按维护用途分组</small>
      </article>
      <article class="mask-summary-card">
        <span>掩码数量</span>
        <strong>{{ totalMaskCount }}</strong>
        <small>当前已保存的过滤规则</small>
      </article>
    </section>

    <section class="config-section manual-entry-section">
      <div class="section-heading">
        <div>
          <h4>手动新增掩码</h4>
          <p>选择规则组并填写掩码和备注，保存成功后可用于未匹配模板下载过滤。</p>
        </div>
        <el-tag type="success" effect="plain">下载规则</el-tag>
      </div>

      <el-form label-position="top" class="manual-entry-form">
        <div class="manual-entry-grid">
          <el-form-item label="规则组">
            <el-select
              v-model="manualForm.groupId"
              class="full-control"
              placeholder="请先新增规则组"
              :disabled="maskGroups.length === 0"
            >
              <el-option
                v-for="group in maskGroups"
                :key="group.id"
                :label="group.name"
                :value="group.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="13 位掩码">
            <el-input
              v-model="manualForm.pattern"
              class="mono-input"
              maxlength="13"
              show-word-limit
              clearable
              placeholder="例如：LVS.FFAL.FS.."
              @keyup.enter="addMask"
            />
          </el-form-item>
          <el-form-item label="备注（选填）">
            <el-input
              v-model="manualForm.remark"
              maxlength="240"
              clearable
              placeholder="选填，例如：Escort 无效在线数据"
              @keyup.enter="addMask"
            />
          </el-form-item>
          <el-form-item label="操作" class="manual-action-item">
            <el-button
              :icon="Plus"
              type="primary"
              class="full-control"
              :loading="maskSaving"
              :disabled="maskGroups.length === 0"
              @click="addMask"
            >
              添加并保存
            </el-button>
          </el-form-item>
        </div>
      </el-form>

      <p class="manual-entry-note">
        掩码必须正好 13 位，只能包含大写字母、数字和“.”；系统会自动转换为大写并检查全局重复。
      </p>
    </section>

    <section class="config-section group-section">
      <div class="section-heading">
        <div>
          <h4>规则组</h4>
          <p>选择规则组后，可以查看和维护其中的具体掩码。</p>
        </div>
        <div class="section-actions">
          <el-tag effect="plain">{{ maskGroups.length }}</el-tag>
          <el-button :icon="Plus" plain @click="openGroupDialog">新增规则组</el-button>
        </div>
      </div>

      <el-table
        :data="maskGroups"
        border
        stripe
        size="small"
        class="rule-table mask-group-table"
        row-key="id"
        empty-text="暂无规则组，请先新增"
        highlight-current-row
        :current-row-key="activeGroupId"
        @row-click="selectGroup"
      >
        <el-table-column prop="name" label="规则组名称" min-width="220" show-overflow-tooltip>
          <template #default="{ row }">
            <strong>{{ row.name }}</strong>
          </template>
        </el-table-column>
        <el-table-column label="掩码数量" width="96" align="center">
          <template #default="{ row }">{{ row.masks.length }}</template>
        </el-table-column>
        <el-table-column prop="remark" label="维护说明" min-width="260" show-overflow-tooltip />
        <el-table-column label="操作" width="132" align="center" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click.stop="selectGroup(row)">查看</el-button>
            <el-popconfirm
              title="确认删除该规则组及组内全部掩码？"
              confirm-button-text="删除"
              cancel-button-text="取消"
              @confirm="removeGroup(row)"
            >
              <template #reference>
                <el-button link type="danger" @click.stop>删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <section v-if="activeGroup" class="config-section mask-detail-section">
      <div class="section-heading">
        <div>
          <h4>{{ activeGroup.name }}</h4>
          <p>{{ activeGroup.remark }}</p>
        </div>
        <el-tag effect="plain">{{ activeGroup.masks.length }} 条</el-tag>
      </div>

      <el-table
        :data="activeGroup.masks"
        border
        stripe
        size="small"
        class="rule-table mask-detail-table"
        empty-text="当前规则组暂无掩码"
      >
        <el-table-column prop="pattern" label="13 位掩码" min-width="200">
          <template #default="{ row }">
            <code class="mono mask-code">{{ row.pattern }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="280" show-overflow-tooltip />
        <el-table-column label="操作" width="82" align="center" fixed="right">
          <template #default="{ row }">
            <el-popconfirm
              title="确认删除这条掩码？"
              confirm-button-text="删除"
              cancel-button-text="取消"
              @confirm="removeMask(row)"
            >
              <template #reference>
                <el-button link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <section v-else class="config-section empty-detail-section">
      <el-empty description="暂无规则组，新增规则组后即可维护掩码" :image-size="72" />
    </section>

    <el-dialog
      v-model="groupDialog.visible"
      class="group-dialog"
      title="新增规则组"
      width="min(560px, 94vw)"
      :close-on-click-modal="false"
    >
      <el-form label-position="top">
        <el-form-item label="规则组名称">
          <el-input
            v-model.trim="groupDialog.name"
            maxlength="80"
            clearable
            placeholder="例如：Escort 无效在线 VIN"
          />
        </el-form-item>
        <el-form-item label="维护说明">
          <el-input
            v-model.trim="groupDialog.remark"
            type="textarea"
            :rows="4"
            maxlength="240"
            show-word-limit
            resize="vertical"
            placeholder="填写规则来源和维护用途"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="groupDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="groupSaving" @click="addGroup">
          添加规则组
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { Plus, Refresh } from '@element-plus/icons-vue';
import {
  createFordVinMask,
  createFordVinMaskGroup,
  deleteFordVinMask,
  deleteFordVinMaskGroup,
  getFordVinMaskGroups,
} from '@/api/users';

const loading = ref(false);
const groupSaving = ref(false);
const maskSaving = ref(false);
const maskGroups = ref([]);
const activeGroupId = ref('');
const manualForm = reactive({
  groupId: '',
  pattern: '',
  remark: '',
});
const groupDialog = reactive({
  visible: false,
  name: '',
  remark: '',
});

const activeGroup = computed(() => (
  maskGroups.value.find((group) => group.id === activeGroupId.value) || null
));
const totalMaskCount = computed(() => (
  maskGroups.value.reduce((sum, group) => sum + group.masks.length, 0)
));

function pickPayload(responseData) {
  if (responseData?.data && typeof responseData.data === 'object') {
    return responseData.data;
  }
  return responseData || {};
}

function responseErrorText(error, fallback) {
  return error?.info
    || error?.response?.data?.message
    || error?.response?.data?.msg
    || error?.message
    || fallback;
}

function normalizeGroup(group) {
  return {
    id: String(group?.id || ''),
    name: String(group?.name || ''),
    remark: String(group?.remark || ''),
    masks: Array.isArray(group?.masks)
      ? group.masks.map((mask) => ({
        pattern: String(mask?.pattern || ''),
        remark: String(mask?.remark || ''),
      }))
      : [],
  };
}

function applyGroups(groups, preferredId = activeGroupId.value) {
  maskGroups.value = Array.isArray(groups)
    ? groups.map(normalizeGroup).filter((group) => group.id && group.name)
    : [];

  const selected = maskGroups.value.find((group) => group.id === preferredId)
    || maskGroups.value[0]
    || null;
  activeGroupId.value = selected?.id || '';
  if (!maskGroups.value.some((group) => group.id === manualForm.groupId)) {
    manualForm.groupId = selected?.id || '';
  }
}

function replaceGroup(group) {
  const normalized = normalizeGroup(group);
  const index = maskGroups.value.findIndex((item) => item.id === normalized.id);
  if (index >= 0) {
    maskGroups.value.splice(index, 1, normalized);
  } else {
    maskGroups.value.push(normalized);
  }
  activeGroupId.value = normalized.id;
  manualForm.groupId = normalized.id;
}

async function fetchGroups() {
  loading.value = true;
  try {
    const response = await getFordVinMaskGroups();
    const payload = pickPayload(response.data);
    applyGroups(payload.groups);
  } catch (error) {
    ElMessage.error(responseErrorText(error, '获取 VIN 掩码规则失败'));
  } finally {
    loading.value = false;
  }
}

function selectGroup(group) {
  activeGroupId.value = group.id;
  manualForm.groupId = group.id;
}

function openGroupDialog() {
  Object.assign(groupDialog, {
    visible: true,
    name: '',
    remark: '',
  });
}

async function addGroup() {
  const name = String(groupDialog.name || '').trim();
  const remark = String(groupDialog.remark || '').trim();
  if (!name) {
    ElMessage.warning('规则组名称不能为空');
    return;
  }
  if (!remark) {
    ElMessage.warning('维护说明不能为空');
    return;
  }
  if (maskGroups.value.some((group) => group.name === name)) {
    ElMessage.warning('规则组名称已存在，请更换后重试');
    return;
  }

  groupSaving.value = true;
  try {
    const response = await createFordVinMaskGroup({ name, remark });
    const payload = pickPayload(response.data);
    if (!payload.group?.id) {
      throw new Error('服务器未返回新增规则组');
    }
    replaceGroup(payload.group);
    groupDialog.visible = false;
    ElMessage.success('规则组已保存');
  } catch (error) {
    ElMessage.error(responseErrorText(error, '新增规则组失败'));
  } finally {
    groupSaving.value = false;
  }
}

async function removeGroup(group) {
  loading.value = true;
  try {
    await deleteFordVinMaskGroup(group.id);
    const nextGroups = maskGroups.value.filter((item) => item.id !== group.id);
    applyGroups(nextGroups, activeGroupId.value === group.id ? '' : activeGroupId.value);
    ElMessage.success('规则组及组内掩码已删除');
  } catch (error) {
    ElMessage.error(responseErrorText(error, '删除规则组失败'));
  } finally {
    loading.value = false;
  }
}

async function addMask() {
  const pattern = String(manualForm.pattern || '').trim().toUpperCase();
  const remark = String(manualForm.remark || '').trim();
  if (!manualForm.groupId) {
    ElMessage.warning('请先选择规则组');
    return;
  }
  if (!/^[A-Z0-9.]{13}$/.test(pattern)) {
    ElMessage.warning('掩码必须正好 13 位，且只能包含字母、数字和“.”');
    return;
  }
  if (maskGroups.value.some((group) => group.masks.some((mask) => mask.pattern === pattern))) {
    ElMessage.warning('该掩码已存在，请勿重复添加');
    return;
  }

  maskSaving.value = true;
  try {
    const response = await createFordVinMask(
      manualForm.groupId,
      { pattern, remark }
    );
    const payload = pickPayload(response.data);
    if (!payload.group?.id) {
      throw new Error('服务器未返回更新后的规则组');
    }
    replaceGroup(payload.group);
    manualForm.pattern = '';
    manualForm.remark = '';
    ElMessage.success('掩码已添加');
  } catch (error) {
    ElMessage.error(responseErrorText(error, '添加掩码失败'));
  } finally {
    maskSaving.value = false;
  }
}

async function removeMask(mask) {
  if (!activeGroup.value) {
    return;
  }

  loading.value = true;
  try {
    const response = await deleteFordVinMask(activeGroup.value.id, mask.pattern);
    const payload = pickPayload(response.data);
    if (!payload.group?.id) {
      throw new Error('服务器未返回更新后的规则组');
    }
    replaceGroup(payload.group);
    ElMessage.success('掩码已删除');
  } catch (error) {
    ElMessage.error(responseErrorText(error, '删除掩码失败'));
  } finally {
    loading.value = false;
  }
}

onMounted(fetchGroups);
</script>

<style scoped>
.vin-mask-rule-panel {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.panel-header,
.config-section,
.mask-summary-card {
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(31, 56, 88, 0.05);
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
}

.panel-header > div:first-child,
.section-heading > div:first-child {
  min-width: 0;
}

.panel-header h3,
.section-heading h4 {
  margin: 0;
  color: #132033;
  font-weight: 650;
}

.panel-header h3 {
  font-size: 17px;
}

.section-heading h4 {
  font-size: 15px;
}

.panel-header p,
.section-heading p {
  margin: 5px 0 0;
  color: #6c7d93;
  font-size: 13px;
  line-height: 1.55;
}

.header-actions,
.section-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.online-alert {
  border-radius: 8px;
}

.mask-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.mask-summary-card {
  display: grid;
  gap: 5px;
  padding: 15px 17px;
}

.mask-summary-card span,
.mask-summary-card small {
  color: #708198;
}

.mask-summary-card span {
  font-size: 13px;
}

.mask-summary-card strong {
  color: #17243a;
  font-size: 25px;
  line-height: 1.1;
}

.mask-summary-card small {
  font-size: 12px;
}

.config-section {
  min-width: 0;
  overflow: hidden;
  padding: 15px 16px 16px;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 13px;
}

.manual-entry-grid {
  display: grid;
  grid-template-columns: minmax(170px, 0.85fr) minmax(210px, 1fr) minmax(230px, 1.2fr) 138px;
  gap: 12px;
  align-items: end;
}

.manual-entry-grid > * {
  min-width: 0;
}

.manual-entry-form :deep(.el-form-item) {
  margin-bottom: 0;
}

.manual-action-item :deep(.el-form-item__content) {
  width: 100%;
}

.full-control {
  width: 100%;
}

.mono,
.mono-input :deep(.el-input__inner) {
  font-family: "Cascadia Mono", Consolas, monospace;
}

.manual-entry-note {
  margin: 10px 0 0;
  color: #75869b;
  font-size: 12px;
  line-height: 1.6;
}

.rule-table {
  width: 100%;
  max-width: 100%;
}

.mask-code {
  color: #1c4e88;
  font-size: 13px;
  font-weight: 650;
  letter-spacing: 0.04em;
}

.empty-detail-section {
  padding-block: 24px;
}

@media (max-width: 980px) {
  .manual-entry-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 680px) {
  .panel-header,
  .section-heading {
    flex-direction: column;
  }

  .mask-summary-grid,
  .manual-entry-grid {
    grid-template-columns: 1fr;
  }

  .header-actions,
  .section-actions {
    width: 100%;
  }
}

@container vin-filter-panel (max-width: 1180px) {
  .manual-entry-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@container vin-filter-panel (max-width: 680px) {
  .panel-header,
  .section-heading {
    flex-direction: column;
  }

  .mask-summary-grid,
  .manual-entry-grid {
    grid-template-columns: 1fr;
  }

  .header-actions,
  .section-actions {
    width: 100%;
  }
}
</style>
